import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import {
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  Event,
  EventImage,
  listEventImages,
  createEventImage,
  updateEventImage,
  deleteEventImage,
  generateEventFlyer,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { toZonedTime, formatInTimeZone } from 'date-fns-tz';

// Helper function to convert UTC datetime to Lisbon timezone for display
const formatDateTimeForDisplay = (utcDateTime: string) => {
  if (!utcDateTime) return '';
  try {
    const date = parseISO(utcDateTime);
    return formatInTimeZone(date, 'Europe/Lisbon', 'dd/MM/yyyy, HH:mm:ss');
  } catch (error) {
    return 'Invalid date';
  }
};

// Helper function to convert Lisbon datetime to UTC for API submission
const convertLisbonToUTC = (lisbonDateTime: string) => {
  if (!lisbonDateTime) return '';
  try {
    // Parse the Lisbon datetime and convert to UTC
    const date = parseISO(lisbonDateTime);
    const utcDate = toZonedTime(date, 'Europe/Lisbon');
    
    return utcDate.toISOString();
  } catch (error) {
    console.error('Error converting Lisbon time to UTC:', error);
    return lisbonDateTime; // Fallback to original value
  }
};

// Helper function to convert UTC datetime to Lisbon timezone for datetime-local input
const formatDateTimeForInput = (utcDateTime: string) => {
  if (!utcDateTime) return '';
  try {
    const date = parseISO(utcDateTime);
    return formatInTimeZone(date, 'Europe/Lisbon', "yyyy-MM-dd'T'HH:mm");
  } catch (error) {
    console.error('Error formatting datetime for input:', error);
    return '';
  }
};

type EditableEventImage = {
  // present if the image already exists on the server
  id?: number;
  name: string;
  // base64-encoded payload (no data:... prefix), used by the API
  image_data: string;
  // tracks the server-side name so we can detect renames on submit
  originalName?: string;
  // marked for deletion on submit
  pendingDelete?: boolean;
};

// Strip "data:<mime>;base64," prefix from a FileReader result
const stripDataUrlPrefix = (s: string) => {
  const comma = s.indexOf(',');
  return comma >= 0 ? s.slice(comma + 1) : s;
};

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(stripDataUrlPrefix(String(reader.result || '')));
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const defaultNameFromFile = (file: File) => {
  const dot = file.name.lastIndexOf('.');
  return dot > 0 ? file.name.slice(0, dot) : file.name;
};

const Events: React.FC = () => {
  const { user: currentUser, loading: authLoading } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [formData, setFormData] = useState<Partial<Event>>({
    name: '',
    starting_at: '',
    ending_at: '',
    description: '',
    event_type: 'music',
    event_other: '',
    organizer: '',
    cost: 0,
    price: 0,
  });
  const [eventImages, setEventImages] = useState<EditableEventImage[]>([]);
  const [imageError, setImageError] = useState('');
  const [generatingFlyer, setGeneratingFlyer] = useState(false);
  const [previewImage, setPreviewImage] = useState<EditableEventImage | null>(null);

  const eventTypes = [
    { value: 'music', label: 'Music' },
    { value: 'poetry', label: 'Poetry' },
    { value: 'lecture', label: 'Lecture' },
    { value: 'dinner', label: 'Dinner' },
    { value: 'party', label: 'Party' },
    { value: 'snooker', label: 'Snooker' },
    { value: 'other', label: 'Other' },
  ];

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await getEvents();
      console.log('📅 Events data received:', response.data.data);
      console.log('📅 First event object:', response.data.data[0]);
      setEvents(response.data.data || []);
      setError('');
    } catch (err: any) {
      console.error('Error fetching events:', err);
      setError(err.response?.data?.message || 'Failed to fetch events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  // Suppress ResizeObserver errors
  useEffect(() => {
    const originalError = console.error;
    console.error = (...args) => {
      if (args[0] && typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };

    return () => {
      console.error = originalError;
    };
  }, []);

  const handleOpenDialog = async (event?: Event) => {
    setImageError('');
    if (event) {
      setEditingEvent(event);
      setFormData({
        name: event.name,
        starting_at: formatDateTimeForInput(event.starting_at), // Convert UTC to Lisbon for input
        ending_at: formatDateTimeForInput(event.ending_at),
        description: event.description,
        event_type: event.event_type,
        event_other: event.event_other || '',
        organizer: event.organizer,
        cost: event.cost || 0,
        price: event.price,
      });
      setEventImages([]);
      try {
        const res = await listEventImages(event.id!);
        const imgs: EventImage[] = res.data?.data || [];
        setEventImages(
          imgs.map((img) => ({
            id: img.id,
            name: img.name,
            image_data: img.image_data,
            originalName: img.name,
          }))
        );
      } catch (err) {
        console.error('Error loading event images:', err);
      }
    } else {
      setEditingEvent(null);
      setFormData({
        name: '',
        starting_at: '',
        ending_at: '',
        description: '',
        event_type: 'music',
        event_other: '',
        organizer: '',
        cost: 0,
        price: 0,
      });
      setEventImages([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEvent(null);
    setFormData({
      name: '',
      starting_at: '',
      ending_at: '',
      description: '',
      event_type: 'music',
      event_other: '',
      organizer: '',
      cost: 0,
      price: 0,
    });
    setEventImages([]);
    setImageError('');
  };

  // Find a name that doesn't collide with existing active images (suffix " (2)", " (3)", ...)
  const uniqueImageName = (base: string, current: EditableEventImage[]) => {
    const taken = new Set(
      current.filter((i) => !i.pendingDelete).map((i) => i.name.trim().toLowerCase())
    );
    if (!taken.has(base.trim().toLowerCase())) return base;
    let n = 2;
    while (taken.has(`${base} (${n})`.toLowerCase())) n++;
    return `${base} (${n})`;
  };

  const handlePickImagesClick = () => fileInputRef.current?.click();

  const handleImageFilesSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setImageError('');
    try {
      const additions: EditableEventImage[] = [];
      const working = [...eventImages];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) {
          setImageError(`"${file.name}" is not an image file`);
          continue;
        }
        const base64 = await readFileAsBase64(file);
        const name = uniqueImageName(defaultNameFromFile(file), [...working, ...additions]);
        additions.push({ name, image_data: base64 });
      }
      setEventImages([...working, ...additions]);
    } catch (err) {
      console.error('Error reading image files:', err);
      setImageError('Failed to read one or more image files');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageNameChange = (idx: number, name: string) => {
    setEventImages((prev) => prev.map((img, i) => (i === idx ? { ...img, name } : img)));
  };

  const handleDownloadImage = (img: EditableEventImage) => {
    try {
      const byteString = atob(img.image_data);
      const bytes = new Uint8Array(byteString.length);
      for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
      const blob = new Blob([bytes], { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${img.name}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error preparing image download:', err);
      setImageError('Failed to prepare image download');
    }
  };

  const handleGenerateFlyer = async () => {
    if (!editingEvent?.id) return;
    setImageError('');
    setGeneratingFlyer(true);
    try {
      const res = await generateEventFlyer(editingEvent.id);
      const refreshed: EventImage[] = res.data?.data?.images || [];
      setEventImages(
        refreshed.map((img) => ({
          id: img.id,
          name: img.name,
          image_data: img.image_data,
          originalName: img.name,
        }))
      );
    } catch (err: any) {
      console.error('Error generating flyer:', err);
      setImageError(err.response?.data?.message || err.message || 'Failed to generate flyer');
    } finally {
      setGeneratingFlyer(false);
    }
  };

  const hasBaseImage = eventImages.some(
    (i) => !i.pendingDelete && i.name.trim().toLowerCase() === 'base'
  );

  const handleImageRemove = (idx: number) => {
    setEventImages((prev) => {
      const img = prev[idx];
      if (img.id) {
        // Existing on server: mark pending delete so we apply on submit
        return prev.map((x, i) => (i === idx ? { ...x, pendingDelete: true } : x));
      }
      // New (not yet uploaded): drop from local state
      return prev.filter((_, i) => i !== idx);
    });
  };

  // Returns null if names are valid, otherwise an error message.
  const validateImageNames = (): string | null => {
    const active = eventImages.filter((i) => !i.pendingDelete);
    const seen = new Set<string>();
    for (const img of active) {
      const name = img.name.trim();
      if (!name) return 'Every image must have a name';
      const key = name.toLowerCase();
      if (seen.has(key)) return 'Image names must be unique within the event';
      seen.add(key);
    }
    return null;
  };

  const syncEventImages = async (eventId: number) => {
    // Deletions first to free up name slots, then renames, then creates.
    for (const img of eventImages) {
      if (img.id && img.pendingDelete) {
        await deleteEventImage(eventId, img.id);
      }
    }
    for (const img of eventImages) {
      if (img.id && !img.pendingDelete && img.originalName !== undefined && img.name.trim() !== img.originalName) {
        await updateEventImage(eventId, img.id, { name: img.name.trim() });
      }
    }
    for (const img of eventImages) {
      if (!img.id && !img.pendingDelete) {
        await createEventImage(eventId, { name: img.name.trim(), image_data: img.image_data });
      }
    }
  };

  const handleSubmit = async () => {
    const nameErr = validateImageNames();
    if (nameErr) {
      setImageError(nameErr);
      return;
    }
    try {
      // Convert Lisbon timezone input to UTC for API submission
      const eventData = {
        ...formData,
        starting_at: convertLisbonToUTC(formData.starting_at || ''),
        ending_at: convertLisbonToUTC(formData.ending_at || ''),
      };

      let eventId: number;
      if (editingEvent) {
        await updateEvent(editingEvent.id!, eventData);
        eventId = editingEvent.id!;
      } else {
        const res = await createEvent(eventData as Event);
        eventId = res.data?.data?.id;
        if (!eventId) {
          throw new Error('Server did not return new event id');
        }
      }

      await syncEventImages(eventId);

      handleCloseDialog();
      fetchEvents();
    } catch (err: any) {
      console.error('Error saving event:', err);
      setError(err.response?.data?.message || err.message || 'Failed to save event');
    }
  };

  const handleDeleteEvent = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      try {
        await deleteEvent(id);
        fetchEvents();
      } catch (err: any) {
        console.error('Error deleting event:', err);
        setError(err.response?.data?.message || 'Failed to delete event');
      }
    }
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: { [key: string]: string } = {
      music: '#1976d2',
      poetry: '#388e3c',
      lecture: '#f57c00',
      dinner: '#d32f2f',
      party: '#7b1fa2',
      snooker: '#455a64',
      other: '#757575',
    };
    return colors[eventType] || '#757575';
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'name', headerName: 'Name', width: 200 },
    { 
      field: 'event_type', 
      headerName: 'Type', 
      width: 120,
      renderCell: (params: any) => (
        <Chip
          label={params.value}
          size="small"
          style={{ 
            backgroundColor: getEventTypeColor(params.value),
            width: '80px',
            justifyContent: 'center',
            color: '#fefefe'
          }}
        />
      ),
    },
    { 
      field: 'event_other', 
      headerName: 'Other Type', 
      width: 120,
      renderCell: (params: any) => params.value || '-',
    },
    { 
      field: 'starting_at', 
      headerName: 'Start (Lisbon)', 
      width: 150,
      renderCell: (params: any) => {
        console.log('📅 Starting date value:', params.value, typeof params.value);
        return formatDateTimeForDisplay(params.value);
      },
    },
    { 
      field: 'ending_at', 
      headerName: 'End (Lisbon)', 
      width: 150,
      renderCell: (params: any) => {
        console.log('📅 Ending date value:', params.value, typeof params.value);
        return formatDateTimeForDisplay(params.value);
      },
    },
    { field: 'organizer', headerName: 'Organizer', width: 150 },
    { 
      field: 'cost', 
      headerName: 'Cost', 
      width: 100,
      renderCell: (params: any) => {
        if (!params.value) return '-';
        const cost = parseFloat(params.value);
        return isNaN(cost) ? '-' : `€${cost.toFixed(2)}`;
      },
    },
    { 
      field: 'price', 
      headerName: 'Price', 
      width: 100,
      renderCell: (params: any) => {
        if (!params.value) return '€0.00';
        const price = parseFloat(params.value);
        return isNaN(price) ? '€0.00' : `€${price.toFixed(2)}`;
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      getActions: (params) => [
        <GridActionsCellItem
          icon={<EditIcon />}
          label="Edit"
          onClick={() => handleOpenDialog(params.row)}
        />,
        <GridActionsCellItem
          icon={<DeleteIcon />}
          label="Delete"
          onClick={() => handleDeleteEvent(params.row.id)}
        />,
      ],
    },
  ];

  // Don't render until authentication is complete
  if (authLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Only show for admin users
  if (!currentUser || !currentUser.is_admin) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <Typography>Access denied. Admin privileges required.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Events Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Event
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setError('')}>
              Dismiss
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      <DataGrid
        rows={events || []}
        columns={columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 10 },
          },
          sorting: {
            sortModel: [{ field: 'starting_at', sort: 'desc' }],
          },
        }}
        autoHeight
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
        }}
      />

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Edit Event' : 'Add New Event'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: '1fr 1fr', mt: 1 }}>
            <TextField
              label="Event Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              fullWidth
            />
            
            <FormControl fullWidth required>
              <InputLabel>Event Type</InputLabel>
              <Select
                value={formData.event_type}
                onChange={(e) => setFormData({ ...formData, event_type: e.target.value as any })}
                label="Event Type"
              >
                {eventTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Starting Date & Time (Lisbon)"
              type="datetime-local"
              value={formData.starting_at}
              onChange={(e) => setFormData({ ...formData, starting_at: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Times are in Europe/Lisbon timezone"
            />

            <TextField
              label="Ending Date & Time (Lisbon)"
              type="datetime-local"
              value={formData.ending_at}
              onChange={(e) => setFormData({ ...formData, ending_at: e.target.value })}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Times are in Europe/Lisbon timezone"
            />

            {formData.event_type === 'other' && (
              <TextField
                label="Other Event Type"
                value={formData.event_other}
                onChange={(e) => setFormData({ ...formData, event_other: e.target.value })}
                fullWidth
                sx={{ gridColumn: '1 / -1' }}
              />
            )}

            <TextField
              label="Organizer Email"
              value={formData.organizer}
              onChange={(e) => setFormData({ ...formData, organizer: e.target.value })}
              required
              fullWidth
            />

            <TextField
              label="Cost (Admin Only)"
              type="number"
              value={formData.cost}
              onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) || 0 })}
              fullWidth
              inputProps={{ step: 0.01, min: 0 }}
            />

            <TextField
              label="Price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              required
              fullWidth
              inputProps={{ step: 0.01, min: 0 }}
            />
          </Box>

          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={4}
            fullWidth
            sx={{ mt: 2 }}
          />

          <Divider sx={{ my: 3 }} />

          <Box>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1} flexWrap="wrap">
              <Typography variant="h6">Images</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {editingEvent && (
                  <Tooltip
                    title={
                      hasBaseImage
                        ? 'Generate flyer images from the "base" photo and event description'
                        : 'Add an image named "base" first'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={handleGenerateFlyer}
                        disabled={!hasBaseImage || generatingFlyer}
                      >
                        {generatingFlyer ? 'Generating…' : 'Generate Flyer'}
                      </Button>
                    </span>
                  </Tooltip>
                )}
                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  onClick={handlePickImagesClick}
                  disabled={generatingFlyer}
                >
                  Add Images
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: 'none' }}
                  onChange={handleImageFilesSelected}
                />
              </Box>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Each image needs a name. Names must be unique within this event.
              {editingEvent && ' "Generate Flyer" runs the Manus skill against the base image and event description; existing images with the same name will be overwritten.'}
            </Typography>

            {imageError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setImageError('')}>
                {imageError}
              </Alert>
            )}

            {eventImages.filter((i) => !i.pendingDelete).length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No images yet.
              </Typography>
            )}

            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              {eventImages.map((img, idx) =>
                img.pendingDelete ? null : (
                  <Box
                    key={img.id ?? `new-${idx}`}
                    sx={{
                      border: 1,
                      borderColor: 'grey.300',
                      borderRadius: 1,
                      p: 1,
                      display: 'flex',
                      gap: 1,
                      alignItems: 'center',
                    }}
                  >
                    <Tooltip title="Click to view full size">
                      <Box
                        component="img"
                        src={`data:image/jpeg;base64,${img.image_data}`}
                        alt={img.name}
                        onClick={() => setPreviewImage(img)}
                        sx={{
                          width: 64,
                          height: 64,
                          objectFit: 'cover',
                          borderRadius: 1,
                          flexShrink: 0,
                          backgroundColor: 'grey.100',
                          cursor: 'pointer',
                          '&:hover': { opacity: 0.85 },
                        }}
                      />
                    </Tooltip>
                    <TextField
                      label="Name"
                      value={img.name}
                      onChange={(e) => handleImageNameChange(idx, e.target.value)}
                      size="small"
                      fullWidth
                      required
                    />
                    <Tooltip title="Download image">
                      <span>
                        <IconButton
                          onClick={() => handleDownloadImage(img)}
                          size="small"
                          disabled={!img.image_data}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Remove image">
                      <IconButton onClick={() => handleImageRemove(idx)} size="small">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={previewImage !== null}
        onClose={() => setPreviewImage(null)}
        maxWidth="lg"
        fullWidth
      >
        {previewImage && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {previewImage.name}
              <IconButton
                aria-label="close preview"
                onClick={() => setPreviewImage(null)}
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ display: 'flex', justifyContent: 'center', backgroundColor: 'grey.900' }}>
              <Box
                component="img"
                src={`data:image/jpeg;base64,${previewImage.image_data}`}
                alt={previewImage.name}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => handleDownloadImage(previewImage)}
              >
                Download
              </Button>
              <Button onClick={() => setPreviewImage(null)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Events;
